import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Relationship Age Gap Calculator: Half-Your-Age-Plus-Seven Rule";
const DESC = "Compute the exact age gap between two partners and validate the classic 'half-your-age-plus-seven' social acceptability rule, with cultural context and legality notes.";

const FAQS = [
  { q: "What is the half-your-age-plus-seven rule?", a: "It's a socially popular guideline stating the youngest acceptable partner age = (your age ÷ 2) + 7. It's a rough rule of thumb, not a legal standard." },
  { q: "Is a 10 year age gap in a relationship acceptable?", a: "Legally yes once both are adults. Socially, 10-year gaps become common past age 30. The half-plus-seven rule flags problematic gaps mostly under age 25." },
  { q: "What's the maximum acceptable partner age by this rule?", a: "Invert it: maximum = (your age − 7) × 2. So a 30-year-old's theoretical upper limit is 46, and a 40-year-old's is 66." },
  { q: "Does the rule apply to same-sex or non-binary couples?", a: "Yes — the arithmetic is identity-neutral. Cultural norms differ, but the ratio itself is the same." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "formula", label: "The Formula" },
  { id: "table", label: "Reference Table" },
  { id: "legality", label: "Legality Notes" },
  { id: "faqs", label: "FAQs" },
];

export const Route = createFileRoute("/age-calculator/relationship-age-gap")({
  head: () => leafHead({
    path: "/age-calculator/relationship-age-gap", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Relationship Age Gap Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Relationship Age Gap", url: "https://drhint.com/age-calculator/relationship-age-gap" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const today = todayISO();
  const ra = useMemo(() => (a ? chronoDiff(a, today) : null), [a, today]);
  const rb = useMemo(() => (b ? chronoDiff(b, today) : null), [b, today]);
  const gap = useMemo(() => (a && b ? chronoDiff(a, b) : null), [a, b]);

  const check = (older: number, younger: number) => {
    const minAllowed = older / 2 + 7;
    return younger >= minAllowed;
  };
  const older = ra && rb ? Math.max(ra.years, rb.years) : 0;
  const younger = ra && rb ? Math.min(ra.years, rb.years) : 0;
  const passes = ra && rb ? check(older, younger) : false;

  const copyText = ra && rb && gap ? `Partner A: ${ra.years}y | Partner B: ${rb.years}y | Gap: ${gap.years}y ${gap.months}m | Half+7: ${passes ? "PASS ✓" : "FLAG ✗"}` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Relationship Age Gap
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Do partners ki DOB daalein — hum exact <strong>age gap</strong> nikaalte hain plus classic <em>half-your-age-plus-seven</em> rule validate karte hain. Chronological maths <Link to="/age-calculator" className="text-primary underline">yahan</Link>.
        </p>
      </section>
      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Partner A — DOB" value={a} onChange={(v) => { setA(v); reset(); }} max={today} />
            <TripleDateInput label="Partner B — DOB" value={b} onChange={(v) => { setB(v); reset(); }} max={today} />
          </div>
          <AnalyzeButton onClick={trigger} disabled={!a || !b || analyzing} label="Check Age Gap" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && ra && rb && gap ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Partner A" value={`${ra.years} yrs`} />
                  <ResultCard label="Partner B" value={`${rb.years} yrs`} />
                  <ResultCard label="Exact Gap" value={`${gap.years}y ${gap.months}m ${gap.days}d`} sub={`${gap.totalDays.toLocaleString()} days apart`} />
                  <ResultCard label="Half+7 Rule" value={passes ? "PASS ✓" : "FLAG ✗"} sub={`Min partner age = ${(older / 2 + 7).toFixed(1)}`} />
                </div>
                <div className="mt-6 rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-5">
                  <div className="text-sm">
                    Older partner ({older} yrs) ki upper limit per <em>2×(age−7)</em> formula = <strong>{(older - 7) * 2} yrs</strong>. Younger partner ({younger} yrs) — {younger <= (older - 7) * 2 ? "within" : "beyond"} the upper cap.
                  </div>
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} /></div>
                <LifeProgressBar dob={a} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter both DOBs and tap Check.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="formula" title="The Half-Your-Age-Plus-7 Formula, Decoded">
        <p>The rule first appeared in French etiquette guides of the 19th century as a light-hearted maximum. Modern usage flips it into a floor: <strong>youngest acceptable partner = (your age ÷ 2) + 7</strong>. It only starts producing meaningful advice past age 14; below that the arithmetic still returns illegal minors.</p>
      </AnchorSection>

      <AnchorSection id="table" title="Quick Reference — Acceptable Ranges">
        <table>
          <thead><tr><th>Your Age</th><th>Youngest OK</th><th>Oldest OK</th></tr></thead>
          <tbody>
            {[20, 25, 30, 35, 40, 45, 50, 60].map((n) => (
              <tr key={n}><td>{n}</td><td>{n / 2 + 7}</td><td>{(n - 7) * 2}</td></tr>
            ))}
          </tbody>
        </table>
      </AnchorSection>

      <AnchorSection id="legality" title="Legal vs. Social — Don't Confuse Them">
        <p>Consent age varies: 18 in India and Pakistan, 16 in the UK, 16-18 across US states. The half-plus-seven rule is a <strong>social heuristic</strong>, never a legal one. Where the rule passes but partners include a minor, local age-of-consent law overrides everything.</p>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}